const GEOAPIFY_API_KEY = '8dbaf973eca949ca92f61db36c4013cc'; // just put the API key here, while we are on free plan

export interface GeoapifyPlace {
  place_id: string;
  name: string;
  formatted: string; // Full formatted address
  lat: number;
  lon: number;
  categories?: string[];
  distance?: number; // Distance from reference point in meters
}

export interface GeoapifySearchResponse {
  features: Array<{
    properties: {
      place_id: string;
      name: string;
      formatted: string;
      lat: number;
      lon: number;
      categories?: string[];
    };
    geometry: {
      coordinates: [number, number]; // [lon, lat]
    };
  }>;
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Search for places using Geoapify Geocoding API
 * @param query Search query string
 * @param proximity Optional reference location [lat, lng] for proximity bias
 * @param limit Maximum number of results (default: 10)
 * @param filters Optional category filters (e.g., 'accommodation', 'commercial', 'entertainment', etc.)
 */
export async function searchPlaces(
  query: string,
  proximity?: { latitude: number; longitude: number },
  limit: number = 10,
  filters?: string[]
): Promise<GeoapifyPlace[]> {
  if (!GEOAPIFY_API_KEY) {
    console.warn('⚠️ Geoapify API key not found. Set REACT_APP_GEOAPIFY_API_KEY in .env');
    return [];
  }

  if (!query || query.trim().length < 2) {
    return [];
  }

  // console.log('🔍 Geoapify search initiated:', { query, proximity, limit });

  try {
    // Build the API URL
    const params = new URLSearchParams({
      text: query.trim(),
      apiKey: GEOAPIFY_API_KEY,
      limit: limit.toString(),
      format: 'geojson',
      lang: 'en',
      // Focus on Australia
      filter: 'countrycode:au',
      // Add proximity bias if available
      ...(proximity && {
        bias: `proximity:${proximity.longitude},${proximity.latitude}`,
      }),
      // Add category filters if provided
      ...(filters &&
        filters.length > 0 && {
          categories: filters.join(','),
        }),
    });

    const url = `https://api.geoapify.com/v1/geocode/search?${params.toString()}`;
    // console.log('📡 Geoapify API call:', url.replace(GEOAPIFY_API_KEY, 'API_KEY_HIDDEN'));

    const response = await fetch(url);
    if (!response.ok) {
      console.error('❌ Geoapify API error:', response.status, response.statusText);
      const errorText = await response.text();
      console.error('Error details:', errorText);
      return [];
    }

    const data: GeoapifySearchResponse = await response.json();
    // console.log('✅ Geoapify response:', data.features?.length || 0, 'features');

    // Transform to our format and calculate distances if proximity is provided
    return data.features.map((feature) => {
      const place: GeoapifyPlace = {
        place_id: feature.properties.place_id,
        name: feature.properties.name || feature.properties.formatted,
        formatted: feature.properties.formatted,
        lat: feature.properties.lat,
        lon: feature.properties.lon,
        categories: feature.properties.categories,
      };

      // Calculate distance if proximity is provided
      if (proximity) {
        place.distance = calculateDistance(proximity.latitude, proximity.longitude, place.lat, place.lon);
      }

      return place;
    });
  } catch (error) {
    console.error('Error searching places with Geoapify:', error);
    return [];
  }
}

/**
 * Reverse geocode coordinates to get place name
 */
export async function reverseGeocode(latitude: number, longitude: number): Promise<string | null> {
  if (!GEOAPIFY_API_KEY) return null;

  try {
    const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&apiKey=${GEOAPIFY_API_KEY}&format=geojson&lang=en`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const data = await response.json();
    const feature = data?.features?.[0];
    return feature?.properties?.formatted || null;
  } catch (error) {
    console.error('Error reverse geocoding with Geoapify:', error);
    return null;
  }
}
